import { DeleteOutline } from '@material-ui/icons';
import DescriptionIcon from '@material-ui/icons/Description';
import { MaterialTableProps } from 'material-table';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { deleteInvoiceFromApi } from '../../../api/accounting';
import TableWithEdit from '../../../components/Tables/TableWithEdit';
import { permissionType } from '../../../constants/permissions_definitions';
import colorPalette from '../../../helpers/colorPalette';
import { GenericObj } from '../../../helpers/customHooks/useButtons';
import { formatNumber } from '../../../helpers/formatNumber';
import { tFormKey, tKey } from '../../../helpers/translate';
import { createInvoiceData, downloadInvoiceDocument } from '../../../redux/accounting/actions';
import { InvoiceListItem } from '../../../redux/accounting/definitions';
import { showSnackbar } from '../../../redux/FeedbackAPI/actions';
import { SeveritySnackbar } from '../../../redux/FeedbackAPI/definitions';
import { selectUserCan } from '../../../redux/permissions/selectors';
import { AppState } from '../../../redux/root-reducer';
import StateCode from '../../../types/Invoices/StateCode';
import useEntityInvoiceList from '../Hooks/useEntityInvoiceList';

interface EntityInvoiceListProps {
  entity_id: number;
  selectInvoice: (invoice_id: number) => void;
  setButtonProperty: (payload: GenericObj) => void;
  downloadInvoiceDocument: (file_id: number, filename: string) => void;
  createInvoice: (entity_id: number) => void;
  userCan: (permission: permissionType) => boolean | undefined;
  showSnackbar: (message: string, severity: SeveritySnackbar) => void;
}

const EntityInvoiceList: React.FC<EntityInvoiceListProps> = ({
  entity_id,
  selectInvoice,
  setButtonProperty,
  downloadInvoiceDocument,
  createInvoice,
  userCan,
  showSnackbar,
}) => {
  const { loading, invoices, errorListMessage, stateFilters, removeInvoice } = useEntityInvoiceList(entity_id);

  const canCreate = userCan('allow_create_invoice');
  const canAccept = userCan('allow_accept_invoice');

  useEffect(() => {
    if (errorListMessage) {
      showSnackbar(errorListMessage, 'error');
    }
  }, [errorListMessage, showSnackbar]);

  useEffect(() => {
    setButtonProperty({
      buttonOnSave: { hidden: true },
      buttonOnSend: { hidden: true },
      buttonOnAccept: { hidden: true },
      buttonOnReject: { hidden: true },
      buttonOnConfirm: { hidden: true },
      buttonToAccounting: { hidden: true },
      buttonOnTracking: { hidden: true },
      buttonAuthorize: { hidden: true },
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOnEditOne = (rowData: InvoiceListItem) => {
    selectInvoice(rowData.id);
  };

  const handleDeleteFunc = (rowData: InvoiceListItem) => {
    deleteInvoiceFromApi(rowData.id).then(() => {
      removeInvoice(rowData.id);
    });
  };

  const actionIcons = {
    Delete: () => <DeleteOutline style={{ color: colorPalette.secondary80 }} />,
    Document: () => <DescriptionIcon style={{ color: 'rgba(0, 0, 0, 0.54)' }} />,
  };

  const tableData: MaterialTableProps<InvoiceListItem> = {
    title: tKey('FACTURAS'),
    columns: [
      {
        field: 'reference_number',
        title: tKey('Factura'),
        //@ts-ignore
        width: '120px',
      },
      {
        field: 'description',
        title: tKey('Descripción'),
      },
      {
        field: 'invoice_date',
        title: tFormKey('Fecha'),
        headerStyle: {
          textAlign: 'center',
        },
        cellStyle: {
          textAlign: 'center',
        },
        render: (rowData: InvoiceListItem) => <div style={{ textAlign: 'center' }}>{rowData.invoice_date}</div>,
        //@ts-ignore
        width: '80px',
      },
      {
        field: 'due_date',
        title: tFormKey('Vencimiento'),
        headerStyle: {
          textAlign: 'center',
        },
        cellStyle: {
          textAlign: 'center',
        },
        render: (rowData: InvoiceListItem) => <div style={{ textAlign: 'center' }}>{rowData.due_date}</div>,
        //@ts-ignore
        width: '80px',
      },
      {
        field: 'total_amount',
        title: tKey('Total'),
        headerStyle: {
          textAlign: 'center',
        },
        cellStyle: {
          textAlign: 'center',
        },
        render: (rowData: InvoiceListItem) => (
          <div style={{ textAlign: 'center' }}>{formatNumber({ value: rowData.total_amount }) + ' €'}</div>
        ),
        //@ts-ignore
        width: '80px',
      },
      {
        field: 'state',
        title: tKey('Estado'),
        //@ts-ignore
        width: '80px',

        lookup: stateFilters,
      },
    ],
    data: invoices,
    options: {
      pageSize: 100,
      pageSizeOptions: [20, 50, 100],
      tableLayout: 'auto',
    },
    actions: [
      (rowData: InvoiceListItem) => ({
        icon: actionIcons.Document,
        tooltip: tFormKey('Documento'),
        hidden: !rowData.file_id,
        onClick: () => downloadInvoiceDocument(rowData.file_id || 0, rowData.filename || ''),
      }),
      (rowData: InvoiceListItem) => ({
        icon: actionIcons.Delete,
        onClick: () => handleDeleteFunc(rowData),
        hidden:
          canCreate || canAccept
            ? !(rowData.state_code === StateCode.EN_CREACION || rowData.state_code === StateCode.PENDIENTE)
            : true,
      }),
    ],
  };

  tableData.isLoading = loading;

  const createNewInvoice = {
    newButtonText: tKey('invoice.actions.new').toLocaleUpperCase(),
    onNew: () => {
      createInvoice(entity_id);
      selectInvoice(0);
    },
  };

  return (
    <TableWithEdit
      tableData={{ ...tableData }}
      key={JSON.stringify(tableData.data)}
      onRowClick={handleOnEditOne}
      filtering
      optionsNew={canCreate ? createNewInvoice : undefined}
      permission
    />
  );
};

const mapStateToProps = (state: AppState) => ({
  userCan: selectUserCan(state),
});

const mapDispatchToProps = (dispatch: any) => ({
  downloadInvoiceDocument: (file_id: number, filename: string) => dispatch(downloadInvoiceDocument(file_id, filename)),
  createInvoice: (entity_id: number) => dispatch(createInvoiceData(entity_id)),
  showSnackbar: (message: string, severity: SeveritySnackbar): void => dispatch(showSnackbar(message, severity)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EntityInvoiceList);
